import { withFocusRing } from "@components/Accessibility/FocusRing/FocusRing";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";
import { ComponentProps } from "@ts/components";
import React, { ChangeEvent, MouseEvent } from "react";
import tw from "twin.macro";
import { Icon } from "../Icon";

type FormRadioProps = Omit<ComponentProps, "name"> & {
  className?: string;
  dataTestId?: string;
  handleChange?: (
    event: MouseEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>,
  ) => void;
  internalState?: boolean;
  label?: string;
  name: string;
  value: boolean;
  tabIndex: number;
};

const Wrapper = tw.div`
  relative flex flex-row items-baseline cursor-pointer
`;
const Label = tw.span`
  inline-block text-hint leading-h5 ml-maxi self-center md:text-body md:ml-maxi
`;
const Input = withFocusRing(tw.input`
  block appearance-none absolute w-full h-full inset-0 cursor-pointer
`);
const StyledIconWrapper = tw.div`
  relative mt-pico h-midi
`;
const StyledIconInner = styledWithInvalidProps("div")(({ display }) => [
  tw`absolute transition-layout duration-fast`,
  display ? tw`block` : tw`hidden`,
]);
const StyledIcon = tw(Icon)``;

const FormRadio = ({
  className,
  dataTestId,
  handleChange,
  internalState,
  label,
  name,
  value,
  tabIndex,
}: FormRadioProps) => {
  const htmlTagRegex = /<.+?>/;
  const createHtmlMarkup = (htmlString) => {
    return { __html: htmlString };
  };
  const isIncludingHtmlTag = label?.match(htmlTagRegex);

  return (
    <Wrapper
      onClick={!internalState ? handleChange : undefined}
      className={className}
    >
      <StyledIconWrapper>
        <StyledIconInner display={value}>
          <StyledIcon name="darkRadioButtonEnabled" size="xxs" />
        </StyledIconInner>
        <StyledIconInner display={!value}>
          <StyledIcon name="darkRadioButton" size="xxs" />
        </StyledIconInner>
      </StyledIconWrapper>
      {isIncludingHtmlTag ? (
        <Label dangerouslySetInnerHTML={createHtmlMarkup(label)} />
      ) : (
        <Label>{label}</Label>
      )}
      {internalState && (
        <Input
          data-testid={dataTestId}
          type="checkbox"
          name={name}
          onChange={handleChange}
          value={value?.toString()}
          tabIndex={tabIndex}
        />
      )}
    </Wrapper>
  );
};

export default FormRadio;
